import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

const SOCKET_URL = 'https://api.zekicalikoglu.info/ws';

let stompClient = null;
let statusSubscription = null;
let friendshipSubscription = null;
let messageSubscription = null;
let groupMessageSubscription = null;
let statusCallbacks = [];
let friendshipCallbacks = [];
let messageCallbacks = [];
let groupMessageCallbacks = [];
let connectAttempts = 0;
const MAX_CONNECT_ATTEMPTS = 5;

// ✅ Token'ı her çağrıda taze al, modül yüklenirken değil
const getToken = () => localStorage.getItem('token');

const getUserFromToken = () => {
  try {
    const token = getToken();
    if (!token) return null;
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) return null;
    const payload = JSON.parse(atob(tokenParts[1]));
    return {
      username: payload.sub,
      id: payload.id || payload.userId
    };
  } catch (e) {
    console.error('Token parse hatası:', e);
    return null;
  }
};

export const connectWebSocket = () => {
  return new Promise((resolve, reject) => {
    connectAttempts++;
    console.log(`WebSocket bağlantısı deneme #${connectAttempts}`);

    // Mevcut bağlantıyı temizle
    if (stompClient) {
      try {
        if (stompClient.active) stompClient.deactivate();
        stompClient = null;
      } catch (e) {
        console.warn('Mevcut bağlantı kapatılırken hata:', e);
      }
    }

    // ✅ Token'ı burada taze çek
    const token = getToken();
    if (!token) {
      console.warn('WebSocket bağlantısı için token bulunamadı');
      return resolve({ connected: false, reason: 'no-token' });
    }

    console.log('SockJS bağlantısı hazırlanıyor:', SOCKET_URL);

    try {
      stompClient = new Client({
        webSocketFactory: () => new SockJS(SOCKET_URL),
        debug: (str) => console.debug('STOMP:', str),
        reconnectDelay: 5000,
        heartbeatIncoming: 25000,
        heartbeatOutgoing: 25000,
        connectHeaders: {
          'Authorization': `Bearer ${token}`
        }
      });

      // ✅ onConnect — STOMP handshake tamamlandığında tetiklenir
      stompClient.onConnect = (frame) => {
        console.log('WebSocket STOMP bağlantısı kuruldu');
        connectAttempts = 0;

        try {
          subscribeToStatusUpdates();
          subscribeToFriendshipUpdates();
          subscribeToMessages();
          resolve({ connected: true, client: stompClient });
        } catch (subscribeError) {
          console.error('Abonelikler sırasında hata:', subscribeError);
          reject(subscribeError);
        }
      };

      stompClient.onStompError = (frame) => {
        const errorMsg = `WebSocket STOMP hatası: ${frame.headers['message']}`;
        console.error(errorMsg);
        reject(new Error(errorMsg));
      };

      stompClient.onDisconnect = () => {
        console.log('WebSocket bağlantısı kesildi');
      };

      stompClient.activate();

    } catch (error) {
      console.error('WebSocket bağlantısı kurulurken hata:', error);
      reject(error);
    }
  });
};

export const disconnectWebSocket = () => {
  statusCallbacks = [];
  friendshipCallbacks = [];
  messageCallbacks = [];
  groupMessageCallbacks = [];

  [statusSubscription, friendshipSubscription, messageSubscription, groupMessageSubscription]
    .forEach(sub => {
      if (sub) {
        try { sub.unsubscribe(); } catch (e) {}
      }
    });

  statusSubscription = null;
  friendshipSubscription = null;
  messageSubscription = null;
  groupMessageSubscription = null;

  if (stompClient && stompClient.active) {
    stompClient.deactivate();
    console.log('WebSocket bağlantısı kapatıldı');
  }

  stompClient = null;
  connectAttempts = 0;
};

const subscribeToStatusUpdates = () => {
  if (!stompClient || !stompClient.active) return;
  statusSubscription = stompClient.subscribe('/topic/status', (message) => {
    try {
      const data = JSON.parse(message.body);
      statusCallbacks.forEach(cb => { try { cb(data); } catch (e) {} });
    } catch (e) {}
  });
};

const subscribeToFriendshipUpdates = () => {
  if (!stompClient || !stompClient.active) return;
  friendshipSubscription = stompClient.subscribe('/topic/friendship', (message) => {
    try {
      const data = JSON.parse(message.body);
      friendshipCallbacks.forEach(cb => { try { cb(data); } catch (e) {} });
    } catch (e) {}
  });
};

const subscribeToMessages = () => {
  if (!stompClient || !stompClient.active) return;

  // ✅ Token'dan kullanıcıyı al
  const user = getUserFromToken();
  if (!user || !user.username) {
    console.error('Mesaj aboneliği için kullanıcı bilgisi alınamadı');
    return;
  }

  // Kullanıcıya özel kuyruk
  const userQueue = `/user/${user.username}/queue/messages`;
  messageSubscription = stompClient.subscribe(userQueue, (message) => {
    try {
      const data = JSON.parse(message.body);
      const enhanced = {
        ...data,
        timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
        isMine: data.senderId === user.id || (data.sender && data.sender.id === user.id)
      };
      messageCallbacks.forEach(cb => { try { cb(enhanced); } catch (e) {} });
    } catch (e) {}
  });

  // Genel mesaj kanalı
  stompClient.subscribe('/topic/messages', (message) => {
    try {
      const data = JSON.parse(message.body);
      const enhanced = {
        ...data,
        timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
        isMine: data.senderId === user.id || (data.sender && data.sender.id === user.id)
      };
      messageCallbacks.forEach(cb => { try { cb(enhanced); } catch (e) {} });
    } catch (e) {}
  });

  console.log('Mesaj abonelikleri kuruldu:', userQueue);
};

export const addStatusUpdateListener = (cb) => typeof cb === 'function' && statusCallbacks.push(cb);
export const removeStatusUpdateListener = (cb) => {
  const i = statusCallbacks.indexOf(cb);
  if (i !== -1) statusCallbacks.splice(i, 1);
};

export const addFriendshipUpdateListener = (cb) => typeof cb === 'function' && friendshipCallbacks.push(cb);
export const removeFriendshipUpdateListener = (cb) => {
  const i = friendshipCallbacks.indexOf(cb);
  if (i !== -1) friendshipCallbacks.splice(i, 1);
};

export const addMessageListener = (cb) => typeof cb === 'function' && messageCallbacks.push(cb);
export const removeMessageListener = (cb) => {
  const i = messageCallbacks.indexOf(cb);
  if (i !== -1) messageCallbacks.splice(i, 1);
};

export const sendStatusUpdate = (userId, username, isOnline) => {
  if (!stompClient || !stompClient.active) return false;
  stompClient.publish({
    destination: '/app/status',
    body: JSON.stringify({ userId, username, online: isOnline, timestamp: new Date() })
  });
  return true;
};

// ✅ STOMP bağlantısı hazır mı kontrolü düzeltildi
export const isConnected = () => {
  try {
    // active = bağlantı başlatıldı, connected = STOMP handshake tamamlandı
    return stompClient !== null && stompClient.active === true && stompClient.connected === true;
  } catch (e) {
    return false;
  }
};

export const isWebSocketConnected = () => isConnected();

export const ensureConnected = async () => {
  if (isConnected()) {
    console.log('WebSocket bağlantısı zaten kurulu');
    return { connected: true };
  }

  // ✅ Token yoksa hiç deneme
  if (!getToken()) {
    console.warn('Token yok, WebSocket bağlantısı kurulamaz');
    return { connected: false, reason: 'no-token' };
  }

  if (window._webSocketConnecting) {
    return new Promise((resolve) => {
      let checks = 0;
      const interval = setInterval(() => {
        checks++;
        if (isConnected()) {
          clearInterval(interval);
          resolve({ connected: true });
        } else if (checks >= 10) {
          clearInterval(interval);
          window._webSocketConnecting = false;
          resolve({ connected: false, reason: 'timeout' });
        }
      }, 500);
    });
  }

  window._webSocketConnecting = true;
  try {
    const result = await connectWebSocket();
    window._webSocketConnecting = false;
    return result;
  } catch (error) {
    window._webSocketConnecting = false;
    return { connected: false, reason: error.message };
  }
};

export const sendChatMessage = async (chatId, content) => {
  if (!chatId || !content) return { success: false, error: 'Geçersiz parametreler' };

  try {
    // ✅ Bağlantı yoksa kur
    if (!isConnected()) {
      const conn = await ensureConnected();
      if (!conn.connected) {
        return { success: false, error: 'WebSocket bağlantısı kurulamadı: ' + (conn.reason || '') };
      }
      // STOMP'un tam hazır olmasını bekle
      await new Promise(r => setTimeout(r, 500));
    }

    // ✅ Hem active hem connected kontrol et
    if (!stompClient || !stompClient.active || !stompClient.connected) {
      return { success: false, error: 'STOMP bağlantısı hazır değil' };
    }

    const chatMessage = {
      chatId,
      content,
      type: 'CHAT',
      timestamp: new Date()
    };

    stompClient.publish({
      destination: '/app/chat.sendMessage',
      body: JSON.stringify(chatMessage),
      headers: {}
    });

    console.log('Mesaj gönderildi:', chatMessage);
    return { success: true, message: chatMessage };

  } catch (error) {
    console.error('Mesaj gönderme hatası:', error);
    return { success: false, error: error.message };
  }
};

export const joinChat = async (chatId) => {
  if (!chatId) return false;
  const numericChatId = Number(chatId);
  if (isNaN(numericChatId)) return false;

  // ✅ Bağlantı yoksa önce kur
  if (!isConnected()) {
    const conn = await ensureConnected();
    if (!conn.connected) return false;
    await new Promise(r => setTimeout(r, 500));
  }

  return sendJoinMessage(numericChatId);
};

const sendJoinMessage = (chatId) => {
  try {
    if (!stompClient || !stompClient.active || !stompClient.connected) {
      console.error('STOMP hazır değil, join gönderilemedi');
      return false;
    }
    stompClient.publish({
      destination: '/app/chat.join',
      body: JSON.stringify({ chatId, type: 'JOIN', timestamp: new Date() }),
      headers: {}
    });
    console.log('Join mesajı gönderildi, chatId:', chatId);
    return true;
  } catch (error) {
    console.error('Join mesajı gönderilirken hata:', error);
    return false;
  }
};

export const getWebSocketConnection = async () => {
  if (isConnected()) return { connected: true, client: stompClient };
  return connectWebSocket();
};

export const subscribeToGroupChannel = (groupId) => {
  if (!stompClient || !stompClient.active || !stompClient.connected) {
    return ensureConnected().then(() => subscribeToGroupChannel(groupId));
  }
  try {
    const destination = `/topic/group/${groupId}`;
    const subscription = stompClient.subscribe(destination, (message) => {
      try {
        const data = JSON.parse(message.body);
        groupMessageCallbacks.forEach(cb => { try { cb(data); } catch (e) {} });
      } catch (e) {}
    });
    console.log('Grup kanalına abone olundu:', destination);
    return subscription;
  } catch (error) {
    console.error('Grup kanal aboneliği hatası:', error);
    return null;
  }
};

export const addGroupMessageListener = (cb) => {
  if (typeof cb === 'function' && !groupMessageCallbacks.includes(cb)) {
    groupMessageCallbacks.push(cb);
  }
};
export const removeGroupMessageListener = (cb) => {
  const i = groupMessageCallbacks.indexOf(cb);
  if (i !== -1) groupMessageCallbacks.splice(i, 1);
};

export const sendGroupMessage = async (groupId, content) => {
  if (!isConnected()) {
    const conn = await ensureConnected();
    if (!conn.connected) throw new Error('WebSocket bağlantısı kurulamadı');
    await new Promise(r => setTimeout(r, 500));
  }
  stompClient.publish({
    destination: `/app/group.message.${groupId}`,
    body: JSON.stringify({ groupId: parseInt(groupId, 10), content, type: 'GROUP' }),
    headers: { 'content-type': 'application/json' }
  });
  console.log(`Grup ${groupId} mesajı gönderildi`);
  return true;
};

export const joinGroup = async (groupId) => {
  if (!isConnected()) {
    const conn = await ensureConnected();
    if (!conn.connected) throw new Error('WebSocket bağlantısı kurulamadı');
    await new Promise(r => setTimeout(r, 500));
  }
  const subscription = await subscribeToGroupChannel(groupId);
  stompClient.publish({
    destination: `/app/group.join.${groupId}`,
    body: JSON.stringify({ groupId: parseInt(groupId, 10), type: 'JOIN' }),
    headers: { 'content-type': 'application/json' }
  });
  console.log(`Grup ${groupId} join gönderildi`);
  return true;
};

export default {
  connectWebSocket,
  disconnectWebSocket,
  addStatusUpdateListener,
  removeStatusUpdateListener,
  addFriendshipUpdateListener,
  removeFriendshipUpdateListener,
  addMessageListener,
  removeMessageListener,
  sendStatusUpdate,
  sendChatMessage,
  joinChat,
  isConnected,
  ensureConnected,
  isWebSocketConnected,
  getWebSocketConnection,
  addGroupMessageListener,
  removeGroupMessageListener,
  sendGroupMessage,
  joinGroup
};